import React from 'react';
import { Link } from 'react-router-dom';
import { FiMapPin, FiCalendar, FiStar, FiTag } from 'react-icons/fi';

// Parse date strings safely: treat YYYY-MM-DD as a local date
const parseDateLocal = (s) => {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const dte = new Date(s);
    return Number.isNaN(dte?.getTime?.()) ? null : dte;
};

const formatDateTime = (dtString, timingText) => {
    try {
        if (!dtString && !timingText) return '';
        const date = dtString ? parseDateLocal(dtString) : null;
        const datePart = date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        return `${datePart}${timingText ? ` - ${timingText}` : ''}`.trim();
    } catch (e) {
        return timingText || '';
    }
};

const priceSummary = (priceOptions = {}) => {
    if (!priceOptions) return '';
    let opts = priceOptions;
    // Normalize if API sends as double-encoded JSON string
    for (let i = 0; i < 2; i += 1) {
        if (typeof opts === 'string') {
            try { opts = JSON.parse(opts); } catch { break; }
        }
    }
    if (!opts || typeof opts !== 'object') return '';
    const toMoney = (val) => {
        const n = parseFloat(val);
        if (Number.isNaN(n)) return null;
        return `$${(n % 1 === 0 ? n.toFixed(0) : n.toFixed(2))}`;
    };

    const entries = Object.entries(opts || {});
    if (!entries.length) return '';

    const map = {};
    entries.forEach(([k, v]) => { map[String(k).toLowerCase()] = v; });
    const single = toMoney(map.single);
    const double = toMoney(map.double);

    if (single || double) {
        const parts = [];
        if (single) parts.push(`Single: ${single}`);
        if (double) parts.push(`Double: ${double}`);
        return parts.join(', ');
    }

    // Fallback: range summary if custom keys only
    const values = entries
        .map(([, v]) => parseFloat(v))
        .filter((v) => !Number.isNaN(v) && v >= 0);
    if (!values.length) return '';
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) return `From $${min}`;
    return `From $${min} - Up to $${max}`;
};

export default function EventCard({ event, onClick, featured = false, to }) {
    const parseArray = (value) => {
        if (Array.isArray(value)) return value;
        try {
            return JSON.parse(value || '[]');
        } catch {
            return [];
        }
    };

    const parseObject = (value) => {
        try {
            return JSON.parse(value || '{}');
        } catch {
            return {};
        }
    };

    const highlights = parseArray(event.highlights);
    const goodToKnow = parseObject(event.good_to_know);

    const whenText = formatDateTime(event.event_datetime, goodToKnow?.timing);
    const priceText = priceSummary(event.price_options);
    const socialLinks = parseArray(event.social_links);
    const ticketUrl = (socialLinks || []).find((l) => typeof l === 'string' && /^https?:\/\//i.test(l));

    const mapsUrl = event?.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}` : '';

    return (
        <div className={`event-card ${featured ? 'event-card--featured' : ''}`} onClick={onClick} role="button" tabIndex={0}>
            <div className="event-card__image" style={{ backgroundImage: `url(${event.media_url || ''})` }} aria-label={event.title || 'Event image'} />
            <div className="event-card__body">
                <h3 className="event-card__title">{event.title || 'Untitled Event'}</h3>
                <div className="event-card__row">
                    <FiMapPin className="event-card__icon" />
                    {event?.location ? (
                        <a className="map-link" href={mapsUrl} target="_blank" rel="noopener noreferrer">{event.location}</a>
                    ) : (
                        <span>Location TBA</span>
                    )}
                </div>
                {whenText && (
                    <div className="event-card__row">
                        <FiCalendar className="event-card__icon event-card__icon--green" />
                        <span>{whenText}</span>
                    </div>
                )}
                {highlights?.length > 0 && (
                    <div className="event-card__row">
                        <FiStar className="event-card__icon event-card__icon--yellow" />
                        <span>{highlights.join(', ')}</span>
                    </div>
                )}
                {priceText && (
                    <div className="event-card__row">
                        <FiTag className="event-card__icon event-card__icon--purple" />
                        <span>{priceText}</span>
                    </div>
                )}
                <div className="event-card__divider"></div>
                {to ? (
                    <Link to={to} className="event-card__cta" onClick={(e) => e.stopPropagation()}>
                        Buy Tickets
                    </Link>
                ) : (
                    <a
                        href={ticketUrl || '#'}
                        className="event-card__cta"
                        onClick={(e) => {
                            if (!ticketUrl) e.preventDefault();
                            e.stopPropagation();
                        }}
                        target={ticketUrl ? '_blank' : undefined}
                        rel={ticketUrl ? 'noopener noreferrer' : undefined}
                    >
                        Buy Tickets
                    </a>
                )}
            </div>
        </div>
    );
}
